import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, User, ArrowLeft, Share2, FileText, Download, X, ChevronLeft, ChevronRight, ZoomIn, Image } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl, isValidImageUrl } from '../utils/imageUrl';
import { getMediumImageUrl, getLargeImageUrl } from '../utils/cloudinaryUpload';

const NewsDetail = () => {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    fetchArticle();
  }, [newsId]);

  useEffect(() => {
    // Extract all images from content and images array when article loads
    if (article) {
      const images = [];
      
      // Add featured image first
      if (isValidImageUrl(article.featured_image)) {
        images.push({
          url: toFullUrl(article.featured_image),
          alt: 'Featured Image',
          type: 'featured'
        });
      }
      
      // Add images from the images array
      if (article.images && article.images.length > 0) {
        article.images.forEach(img => {
          if (isValidImageUrl(img.url)) {
            images.push({
              url: toFullUrl(img.url),
              alt: img.alt || 'Article Image',
              type: 'gallery'
            });
          }
        });
      }
      
      // Extract images from content HTML
      const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*(?:alt=["']([^"']*)["'])?/gi;
      let match;
      while ((match = imgRegex.exec(article.content || '')) !== null) {
        const imgUrl = match[1];
        const imgAlt = match[2] || 'Article Image';
        // Avoid duplicates
        if (!images.some(img => img.url === imgUrl)) {
          images.push({
            url: imgUrl,
            alt: imgAlt,
            type: 'content'
          });
        }
      }
      
      setAllImages(images);
    }
  }, [article]);

  const fetchArticle = async () => {
    try {
      const response = await api.get(`/news/${newsId}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Failed to load article:', error);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (imageUrl, index = 0) => {
    setLightboxImage(imageUrl);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < allImages.length) {
      setLightboxIndex(newIndex);
      setLightboxImage(allImages[newIndex].url);
    }
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, lightboxIndex]);

  // Add click handlers to images in content after render
  useEffect(() => {
    if (article) {
      const contentDiv = document.querySelector('.article-content');
      if (contentDiv) {
        const images = contentDiv.querySelectorAll('img');
        images.forEach((img) => {
          img.style.cursor = 'pointer';
          img.title = 'Click to enlarge';
          img.onclick = () => {
            const imgIndex = allImages.findIndex(i => i.url === img.src);
            openLightbox(img.src, imgIndex >= 0 ? imgIndex : 0);
          };
        });
      }
    }
  }, [article, allImages]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: url,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <Card className="max-w-md w-full mx-4">
            <CardContent className="p-8 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Article Not Found</h2>
              <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
              <Button onClick={() => navigate('/news')} className="bg-cyan-500 hover:bg-cyan-600">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to News
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Hero Image - clickable to open lightbox */}
        <div 
          className="w-full h-48 sm:h-64 md:h-96 relative cursor-pointer group"
          onClick={() => {
            const heroUrl = isValidImageUrl(article.featured_image) 
              ? toFullUrl(article.featured_image) 
              : 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800';
            openLightbox(heroUrl, 0);
          }}
        >
          <img
            src={isValidImageUrl(article.featured_image) ? toFullUrl(article.featured_image) : 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800'}
            alt={article.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-[1.02]"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.pexels.com/photos/1554646/pexels-photo-1554646.jpeg?auto=compress&cs=tinysrgb&w=800';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 bg-black/50 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm flex items-center gap-1 sm:gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Click to enlarge</span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/news')}
            className="mb-4 sm:mb-6 text-slate-600 hover:text-slate-900 text-sm sm:text-base px-2 sm:px-4"
            data-testid="back-to-news"
          >
            <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Back to News
          </Button>

          {/* Article Header */}
          <article className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-10">
            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                {article.tags.map(tag => (
                  <Badge key={tag} className="bg-cyan-100 text-cyan-700 text-xs sm:text-sm">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900 mb-3 sm:mb-4">
              {article.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-slate-200">
              <div className="flex items-center gap-1 sm:gap-2">
                <User className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>{article.author_name || 'CTC Editorial'}</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>{new Date(article.published_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="ml-auto text-slate-500 hover:text-cyan-600 text-xs sm:text-sm px-2 sm:px-3"
              >
                <Share2 className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                Share
              </Button>
            </div>

            {/* Excerpt */}
            {article.excerpt && (
              <p className="text-sm sm:text-base md:text-lg text-slate-600 mb-4 sm:mb-6 font-medium leading-relaxed">
                {article.excerpt}
              </p>
            )}

            {/* Main Content */}
            <div 
              className="article-content prose prose-sm sm:prose prose-slate max-w-none 
                prose-headings:font-outfit prose-headings:text-slate-900 prose-headings:font-semibold
                prose-h2:text-lg sm:prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-6 sm:prose-h2:mt-8 prose-h2:mb-3 sm:prose-h2:mb-4
                prose-h3:text-base sm:prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-4 sm:prose-h3:mt-6 prose-h3:mb-2 sm:prose-h3:mb-3
                prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-3 sm:prose-p:mb-4 prose-p:text-sm sm:prose-p:text-base
                prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline
                prose-img:rounded-lg sm:prose-img:rounded-xl prose-img:shadow-lg prose-img:my-4 sm:prose-img:my-6 prose-img:cursor-pointer prose-img:transition-transform hover:prose-img:scale-[1.01]
                prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 prose-blockquote:bg-slate-50 
                prose-blockquote:py-2 prose-blockquote:px-3 sm:prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:text-sm sm:prose-blockquote:text-base
                prose-figure:my-4 sm:prose-figure:my-8 prose-figcaption:text-center prose-figcaption:text-xs sm:prose-figcaption:text-sm prose-figcaption:text-slate-500 prose-figcaption:mt-2
                prose-ul:my-3 sm:prose-ul:my-4 prose-ul:list-disc prose-ul:pl-4 sm:prose-ul:pl-6 prose-ol:my-3 sm:prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-4 sm:prose-ol:pl-6 prose-li:text-slate-700 prose-li:my-1 prose-li:text-sm sm:prose-li:text-base
                prose-strong:text-slate-900"
              dangerouslySetInnerHTML={{ __html: article.content || '' }}
            />

            {/* Image Gallery Section */}
            {allImages.length > 1 && (
              <div className="mt-10 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Image className="h-5 w-5 text-cyan-500" />
                  Article Images ({allImages.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {allImages.map((img, index) => (
                    <div 
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group shadow-sm hover:shadow-md transition-shadow"
                      onClick={() => openLightbox(img.url, index)}
                      data-testid={`gallery-image-${index}`}
                    >
                      <img
                        src={getMediumImageUrl(img.url, 300)}
                        alt={img.alt}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                      </div>
                      {img.type === 'featured' && (
                        <div className="absolute top-2 left-2 bg-cyan-500 text-white text-xs px-2 py-0.5 rounded">
                          Featured
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {article.attachments && article.attachments.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-ocean-900 mb-4">Attachments</h3>
                <div className="grid gap-3">
                  {article.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <FileText className="h-5 w-5 text-cyan-500" />
                      <span className="text-sm text-slate-700 flex-1">{attachment.filename}</span>
                      <Download className="h-4 w-4 text-slate-400" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Related Articles CTA */}
          <div className="mt-8 text-center">
            <Link to="/news">
              <Button variant="outline" className="border-cyan-500 text-cyan-600 hover:bg-cyan-50">
                View More News
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLightbox();
          }}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation Arrows */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={() => navigateLightbox(-1)}
                disabled={lightboxIndex === 0}
                className={`absolute left-4 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors ${
                  lightboxIndex === 0 ? 'opacity-30 cursor-not-allowed' : ''
                }`}
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={() => navigateLightbox(1)}
                disabled={lightboxIndex === allImages.length - 1}
                className={`absolute right-4 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors ${
                  lightboxIndex === allImages.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                }`}
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div className="flex items-center justify-center p-4 sm:p-8 w-full h-full max-w-6xl mx-auto">
            {lightboxImage && (
              <img
                src={getLargeImageUrl(lightboxImage, 1600)}
                alt={allImages[lightboxIndex]?.alt || 'Article image'}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                data-testid="lightbox-image"
              />
            )}
          </div>

          {/* Image Counter & Caption */}
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-center">
            <div className="bg-black/60 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
              {lightboxIndex + 1} / {allImages.length}
              {allImages[lightboxIndex]?.alt && allImages[lightboxIndex].alt !== 'Article Image' && (
                <span className="ml-2 opacity-80">• {allImages[lightboxIndex].alt}</span>
              )}
            </div>
          </div>

          {/* Thumbnail Strip */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black/60 p-2 rounded-xl max-w-[90vw] overflow-x-auto backdrop-blur-sm">
              {allImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setLightboxIndex(index);
                    setLightboxImage(img.url);
                  }}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all border-2 ${
                    index === lightboxIndex 
                      ? 'border-cyan-500 opacity-100 scale-105' 
                      : 'border-transparent opacity-60 hover:opacity-90'
                  }`}
                >
                  <img
                    src={getMediumImageUrl(img.url, 100)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default NewsDetail;
